import { useEffect, useState } from "react";
import "./IssueDetail.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Icon } from "./Icon";
import { Chip, StatusPill } from "./Status";
import { IssueActions } from "./IssueActions";
import { IssueQuestions, IssueSpecEditor, SupplementCard } from "./IssueEdit";
import { IssueTabs } from "./IssueTabs";
import { complexityText, minutesSince, priorityText } from "./IssueCard";
import type { Issue } from "../lib/tasks";
import { isHeld, isMerging, isWorking } from "../lib/tasks";
import { useScrollLock } from "../lib/scrollLock";
import { useMe } from "../app/AuthGate";
import { tx } from "../i18n";

/* ══ 이슈 상세 — 모달과 전체 화면이 같은 몸통을 쓴다(t-in-port-task-detail) ══
   보드 · 목록 · 대기열 · 그래프 어디서 눌러도 같은 상세가 뜬다. 몸통(`IssueDetailBody`)이 한 벌이고,
   모달(`IssueDetailModal`)과 전체 화면(`#/issue?id=`, `pages/IssuePage.tsx`)은 그 몸통을 감싸는 틀만 다르다.

   b3x-pr 상세와 비교해 더한 것(docs/screens/pr-port.md §2): 모달 안 뒤로/앞으로 · 메타 줄(id · 프로젝트 · 담당 ·
   에이전트 · 만든 때) · 전체 화면으로 열기 · 주소 복사 · 바닥 닫기. 일부러 안 더한 것:
   기획/검토 복잡도 · 이슈별 벤더 · 시도 n/3 · 맥락 탭 · 검증 — b3x-in 이슈에 그 칸이 없다(컬럼을 화면이
   먼저 요구하지 않는다, AGENTS.md 「이슈 모델」). */

/** 상세 주소 — 「주소 복사」와 「전체 화면으로 열기」가 같은 것을 쓴다. */
export const issueHref = (id: string) => `#/issue?id=${encodeURIComponent(id)}`;

/** 메타 줄 — 한 줄의 사실. 없는 값은 줄에서 빼지 않고 「없음」이라 적는다(「모름」과 섞지 않는다). */
export function MetaLine({ t, projectLabel }: { t: Issue; projectLabel?: string }) {
  const none = tx("common.state.none");
  const items: [string, string][] = [
    ["id", t.id],
    [tx("issueDetail.project"), t.projectId ? (projectLabel ?? t.projectId) : none],
    [tx("board.detail.assignee"), t.assignee ?? none],
    // 에이전트는 **열린 세션**이다 — 상태에서 파생시키지 않는다.
    [tx("issueDetail.agent"), t.openSession ? t.openSession.runner : none],
    [tx("issueTabs.createdAt"), t.createdAt ? new Date(t.createdAt).toLocaleString() : tx("issueTabs.unknown")],
  ];
  return (
    <dl className="idetail__meta" aria-label={tx("issueDetail.meta")}>
      {items.map(([k, v]) => (
        <div key={k} className="idetail__metaitem">
          <dt>{k}</dt>
          <dd className={k === "id" ? "tnum" : undefined}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** 몸통 — 칩 · 메타 · 안내 · 조작 줄(상위 이슈 안내 띠는 조작 줄이 이미 말한다 — 두 번 적지 않는다) · 질문 · 보완 · 탭(또는 편집). 틀(모달 · 전체 화면)은 부르는 쪽이다. */
export function IssueDetailBody({
  t,
  all,
  onOpen,
  epicTitle,
  milestoneLabel,
  projectLabel,
  onChanged,
  onDeleted,
}: {
  t: Issue;
  all: Issue[];
  onOpen: (id: string) => void;
  epicTitle?: string;
  milestoneLabel?: string;
  projectLabel?: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const me = useMe()?.me.username;
  const [editing, setEditing] = useState(false);
  // 다른 이슈로 갈아타면 편집을 접는다 — 고치던 값이 다른 이슈에 실리면 안 된다.
  useEffect(() => setEditing(false), [t.id]);
  return (
    <div className="dmodal__body">
      <div className="dmodal__chips">
        <StatusPill status={t.status} working={isWorking(t)} />
        {isHeld(t) ? <Chip tone="accent">◆ {tx("issue.held")}</Chip> : null}
        {isMerging(t) ? <Chip tone="success">⧉ {tx("issue.merging")}</Chip> : null}
        <Chip>{t.type ?? tx("issue.noType")}</Chip>
        <Chip>{priorityText(t.priority)}</Chip>
        <Chip>{complexityText(t.complexity)}</Chip>
        {!editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            ✎ {tx("issueEdit.edit")}
          </Button>
        ) : null}
      </div>
      <MetaLine t={t} projectLabel={projectLabel} />

      {t.status === "blocked" ? (
        <p className="dmodal__blocked">
          {t.blockedNote ? tx("issueActs.blockedNote", { note: t.blockedNote }) : tx("issueActs.blockedNoteNone")}
        </p>
      ) : null}

      {t.openSession ? (
        <Alert tone="info">
          {tx("board.detail.running", { runner: t.openSession.runner, since: minutesSince(t.openSession.startedAt) })}
          {t.status === "blocked" ? ` ${tx("board.detail.blockedOpen")}` : ""}
        </Alert>
      ) : null}

      {/* 조작 줄은 탭 **위**다(Figma 33:2) — 어느 탭을 보든 손이 닿아야 한다.
          key — 다른 이슈로 갈아타면 펼친 확인창 · 오류가 따라오지 않게 새로 세운다. */}
      <IssueActions key={t.id} t={t} me={me} onChanged={onChanged} onDeleted={onDeleted} />

      <IssueQuestions key={`q-${t.id}`} t={t} onChanged={onChanged} />
      <SupplementCard key={`s-${t.id}`} t={t} onChanged={onChanged} />

      {editing ? (
        <IssueSpecEditor
          key={`e-${t.id}`}
          t={t}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      ) : (
        <IssueTabs t={t} all={all} onOpen={onOpen} onChanged={onChanged} epicTitle={epicTitle} milestoneLabel={milestoneLabel} />
      )}
    </div>
  );
}

/** 「주소 복사」 — 됐는지 · 못 했는지를 단추 글자로 잠깐 말한다(토스트를 안 띄운다 — 눌린 자리에서 보인다). */
export function CopyLinkButton({ id }: { id: string }) {
  const [said, setSaid] = useState<"" | "ok" | "fail">("");
  useEffect(() => {
    if (!said) return;
    const h = setTimeout(() => setSaid(""), 2000);
    return () => clearTimeout(h);
  }, [said]);
  const url = `${location.origin}${location.pathname}${issueHref(id)}`;
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        navigator.clipboard
          ?.writeText(url)
          .then(() => setSaid("ok"))
          .catch(() => setSaid("fail"));
        if (!navigator.clipboard) setSaid("fail");
      }}
      title={url}
    >
      {said === "ok" ? tx("issueDetail.copied") : said === "fail" ? tx("issueDetail.copyFailed") : tx("issueDetail.copyLink")}
    </Button>
  );
}

/** 모달 — 머리에 뒤로/앞으로(모달 안에서 선행 · 하위 · 후속을 타고 간 길) · 전체 화면 · 닫기, 바닥에 닫기.
 *  길은 **모달 안에만** 산다 — 주소(해시)에 싣지 않는다. 폰의 뒤로 제스처가 모달 안 이동까지 되감아야 하는
 *  일이 생기지 않게(b3x-pr 이 해시로 했다가 되돌린 이유). 새로 열면 길이 비워진다. */
export function IssueDetailModal({
  t,
  all,
  onOpen,
  onClose,
  ...rest
}: {
  t: Issue;
  all: Issue[];
  onOpen: (id: string) => void;
  onClose: () => void;
  epicTitle?: string;
  milestoneLabel?: string;
  projectLabel?: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  useScrollLock();
  const [back, setBack] = useState<string[]>([]);
  const [fwd, setFwd] = useState<string[]>([]);
  const go = (id: string) => {
    if (id === t.id) return;
    setBack((b) => [...b, t.id]);
    setFwd([]);
    onOpen(id);
  };
  const goBack = () => {
    const prev = back[back.length - 1];
    if (!prev) return;
    setBack((b) => b.slice(0, -1));
    setFwd((f) => [t.id, ...f]);
    onOpen(prev);
  };
  const goFwd = () => {
    const next = fwd[0];
    if (!next) return;
    setFwd((f) => f.slice(1));
    setBack((b) => [...b, t.id]);
    onOpen(next);
  };
  return (
    <div className="b-dialog-layer" onClick={onClose}>
      <div className="b-scrim" aria-hidden="true" />
      <div className="b-dialog dmodal" role="dialog" aria-modal="true" aria-label={t.title} onClick={(e) => e.stopPropagation()}>
        <header className="b-dialog-head idetail__head">
          <span className="idetail__nav">
            <Button icon variant="ghost" size="sm" onClick={goBack} disabled={!back.length} aria-label={tx("issueDetail.back")} title={tx("issueDetail.back")}>
              <Icon name="chevronLeft" />
            </Button>
            <Button icon variant="ghost" size="sm" onClick={goFwd} disabled={!fwd.length} aria-label={tx("issueDetail.forward")} title={tx("issueDetail.forward")}>
              <Icon name="chevronRight" />
            </Button>
          </span>
          <h2 className="b-dialog-title idetail__title">{t.title}</h2>
          <a className="b-ibtn b-btn-ghost" href={issueHref(t.id)} aria-label={tx("issueDetail.full")} title={tx("issueDetail.full")}>
            <Icon name="maximize" />
          </a>
          <Button icon variant="ghost" onClick={onClose} aria-label={tx("common.action.close")}>
            <Icon name="x" />
          </Button>
        </header>
        <IssueDetailBody t={t} all={all} onOpen={go} {...rest} />
        <footer className="b-dialog-foot">
          <CopyLinkButton id={t.id} />
          <Button onClick={onClose}>{tx("common.action.close")}</Button>
        </footer>
      </div>
    </div>
  );
}
